import styled from '@emotion/styled';
import Image from 'next/image';
import { getImage } from '@utils/imageHelper';
import { mediaQuery } from '@styles/index';
import { REFERRAL_PROMOTIONS_STATIC_IMAGES } from './feature';
import type { PrizeKey, Quote } from './types';

export const campaignImage = (name: string) =>
  getImage(`${REFERRAL_PROMOTIONS_STATIC_IMAGES}/${name}`);

export const avatarImage = (index: number) => {
  const file =
    index === 7 || index === 8 ? `avatar-${index}.png` : `avatar-${index}.svg`;
  return campaignImage(file);
};

const PRIZE_POSITION: Record<PrizeKey, string> = {
  hunter: '0% 100%',
  ps5: '0% 0%',
  euro: '100% 0%',
  macbook: '100% 100%'
};

/** Center of each 3:2 quadrant, used to crop the sprite without distorting it. */
const PRIZE_FOCUS: Record<PrizeKey, string> = {
  hunter: '-25%, -75%',
  ps5: '-25%, -25%',
  euro: '-75%, -25%',
  macbook: '-75%, -75%'
};

export const PageShell = styled.div`
  min-height: 100vh;
  background: #090102;
  color: #fff;
  overflow-x: hidden;
`;

export const Content = styled.div<{ $afterHero?: 'poll' | 'squad' | 'reveal' }>`
  width: min(1136px, calc(100% - 48px));
  margin: 0 auto 64px;

  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: calc(100% - 20px);
    margin: ${({ $afterHero }) => {
      if ($afterHero === 'squad') return '67px auto 32px';
      if ($afterHero === 'reveal') return '40px auto 32px';
      return '25px auto 32px';
    }};
  }
`;

export const Panel = styled.section`
  position: relative;
  background: #120305;
  border: 1px solid rgba(100, 16, 24, 0.5);
  box-shadow: inset 0 0 49px rgba(245, 28, 61, 0.1);
  border-radius: 20px;
  padding: 36px 24px;
  margin-top: 24px;

  ${mediaQuery.uptoMobile} {
    padding: 24px 12px;
    margin-top: 0;
    border-radius: 20px;
  }
`;

export const PollEyebrowIcon = () => (
  <Image src={campaignImage('poll-icon.svg')} alt="" width={24} height={24} />
);

export const SparkleIcon = () => (
  <Image src={campaignImage('sparkle.svg')} alt="" width={31} height={31} />
);

export const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px;
  line-height: 1;
  white-space: nowrap;

  img {
    width: 24px;
    height: 24px;
  }

  ${mediaQuery.uptoMobile} {
    gap: 8px;
    font-size: 12px;

    img {
      width: 18px;
      height: 18px;
    }
  }
`;

export const SectionTitle = styled.h2`
  margin: 14px 0 0;
  font-size: 32px;
  font-weight: 600;
  line-height: 1.1;
  color: #fff;

  ${mediaQuery.uptoMobile} {
    margin-top: 8px;
    font-size: 24px;
    line-height: 1.002;
    font-weight: 600;
  }
`;

const PrizeArtFrame = styled.div<{ $tall?: boolean; $compact?: boolean }>`
  width: 100%;
  height: ${({ $tall }) => ($tall ? '155px' : '145px')};
  border-radius: 12px;
  background-color: #130305;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;

  ${({ $compact, $tall }) =>
    $compact && !$tall
      ? `
    ${mediaQuery.uptoTablet} {
      height: 88px;
      border-radius: 4px;
    }
  `
      : ''}

  ${mediaQuery.uptoMobile} {
    height: ${({ $tall }) => ($tall ? '195px' : '88px')};
    border-radius: ${({ $tall }) => ($tall ? '8px' : '4px')};
  }
`;

const PrizeArtImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
`;

const PrizeArtSprite = styled.div<{
  $prizeKey: PrizeKey;
  $tall?: boolean;
  $compact?: boolean;
}>`
  width: 100%;
  height: ${({ $tall }) => ($tall ? '155px' : '145px')};
  border-radius: 12px;
  background-color: #130305;
  background-image: url(${campaignImage('prizes.png')});
  background-size: 200% auto;
  background-position: ${({ $prizeKey }) => PRIZE_POSITION[$prizeKey]};
  background-repeat: no-repeat;
  flex-shrink: 0;

  ${({ $compact, $tall, $prizeKey }) =>
    $compact && !$tall
      ? `
    ${mediaQuery.uptoTablet} {
      height: 88px;
      border-radius: 4px;
      background-image: none;
      overflow: hidden;
      position: relative;

      &::before {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        width: max(200%, 264px);
        aspect-ratio: 3 / 2;
        transform: translate(${PRIZE_FOCUS[$prizeKey]});
        background-image: url(${campaignImage('prizes.png')});
        background-size: 100% 100%;
        background-repeat: no-repeat;
      }
    }
  `
      : ''}

  ${mediaQuery.uptoMobile} {
    height: ${({ $tall }) => ($tall ? '195px' : '88px')};
    border-radius: ${({ $tall }) => ($tall ? '8px' : '4px')};
  }
`;

export const PrizeArt = ({
  src,
  prizeKey,
  tall,
  compact
}: {
  src?: string;
  prizeKey?: PrizeKey;
  tall?: boolean;
  compact?: boolean;
}) => {
  if (src) {
    return (
      <PrizeArtFrame $tall={tall} $compact={compact}>
        <PrizeArtImg src={src} alt="" />
      </PrizeArtFrame>
    );
  }

  if (prizeKey) {
    return (
      <PrizeArtSprite $prizeKey={prizeKey} $tall={tall} $compact={compact} />
    );
  }

  return <PrizeArtFrame $tall={tall} $compact={compact} />;
};

export const AvatarImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: block;
`;

const DesktopCopy = styled.span`
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const MobileCopy = styled.span`
  display: none;

  ${mediaQuery.uptoMobile} {
    display: inline;
  }
`;

const QuotesLayout = styled.div`
  display: grid;
  grid-template-columns: 252px 1fr;
  gap: 46px;
  align-items: start;

  ${mediaQuery.uptoTablet} {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  ${mediaQuery.uptoMobile} {
    gap: 36px;
  }
`;

const QuoteGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 18px;

  ${mediaQuery.uptoMobile} {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const QuoteCard = styled.article`
  background: #211113;
  border: 1px solid #461b20;
  border-radius: 16px;
  padding: 16px;
  min-height: 143px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${mediaQuery.uptoMobile} {
    min-height: 132px;
    padding: 13px;
    border-radius: 13px;
    width: 100%;
    border-width: 0.8px;
  }
`;

const QuoteBody = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;

  ${mediaQuery.uptoMobile} {
    gap: 11px;
  }
`;

const QuoteMark = styled.span`
  flex-shrink: 0;
  width: 30px;
  font-size: 64px;
  line-height: 0.84;
  letter-spacing: 0.01em;
  background: linear-gradient(180deg, #e06a7a 0%, #d7576c 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;

  ${mediaQuery.uptoMobile} {
    width: 24px;
    font-size: 60px;
    letter-spacing: 0.01em;
  }
`;

const QuoteText = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.3;
  letter-spacing: 0.01em;
  color: #fff;
  white-space: pre-wrap;

  ${mediaQuery.uptoMobile} {
    font-size: 16px;
    line-height: 1.3;
    letter-spacing: 0.01em;
  }
`;

const QuoteAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;

  ${mediaQuery.uptoMobile} {
    gap: 10px;
    margin-top: 12px;
  }
`;

const QuoteAvatar = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #d9d9d9;
  flex-shrink: 0;

  ${mediaQuery.uptoMobile} {
    width: 32px;
    height: 32px;
  }
`;

const QuoteAvatarImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: #d9d9d9;
  flex-shrink: 0;
  display: block;

  ${mediaQuery.uptoMobile} {
    width: 32px;
    height: 32px;
  }
`;

const QuoteName = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 600;

  ${mediaQuery.uptoMobile} {
    font-size: 13px;
    line-height: 1;
  }
`;

const QuoteTag = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);

  ${mediaQuery.uptoMobile} {
    margin-top: 5px;
    font-size: 9.5px;
    line-height: 1;
  }
`;

const QuotesIntro = styled.div`
  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;

    h2 {
      margin-top: 0;
    }
  }
`;

export const QuotesSection = ({ quotes }: { quotes: Quote[] }) => {
  if (!quotes.length) return null;

  return (
    <Panel>
      <QuotesLayout>
        <QuotesIntro>
          <Eyebrow>
            <PollEyebrowIcon />
            <DesktopCopy>What students are saying</DesktopCopy>
            <MobileCopy>What students are saving</MobileCopy>
          </Eyebrow>
          <SectionTitle>More friends, More chances.</SectionTitle>
        </QuotesIntro>
        <QuoteGrid>
          {quotes.map((quote) => (
            <QuoteCard key={quote.id}>
              <QuoteBody>
                <QuoteMark>“</QuoteMark>
                <QuoteText>{quote.text}</QuoteText>
              </QuoteBody>
              <QuoteAuthor>
                {quote.avatarUrl ? (
                  <QuoteAvatarImage src={quote.avatarUrl} alt="" />
                ) : (
                  <QuoteAvatar />
                )}
                <div>
                  <QuoteName>{quote.name}</QuoteName>
                  <QuoteTag>{quote.tag}</QuoteTag>
                </div>
              </QuoteAuthor>
            </QuoteCard>
          ))}
        </QuoteGrid>
      </QuotesLayout>
    </Panel>
  );
};

export const IndiaFlag = () => (
  <img src={campaignImage('india-flag.png')} alt="" width={32} height={24} />
);
